class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    const intents = intent === 'related' ? ['complementary', 'related'] : [intent];

    this.#fetchCachedRecommendations(productId, sectionId, intents)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const recommendations = this.#prepareRecommendations(result.data, id);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Builds the rendered recommendation section and places unique complementary products first.
   * @param {string[]} responses
   * @param {string} id
   * @returns {HTMLElement | null}
   */
  #prepareRecommendations(responses, id) {
    const recommendations = responses.map((response) => {
      const html = document.createElement('div');
      html.innerHTML = response || '';
      return html.querySelector(`product-recommendations[id="${id}"]`);
    });

    if (recommendations.length < 2) {
      return recommendations[0];
    }

    const [complementaryRecommendations, relatedRecommendations] = recommendations;
    const complementaryList = complementaryRecommendations?.querySelector('[data-testid="resource-list-grid"]');
    const relatedList = relatedRecommendations?.querySelector('[data-testid="resource-list-grid"]');

    if (!relatedList || !complementaryList) {
      return relatedRecommendations;
    }

    const maxProducts = Number(new URL(this.dataset.url, window.location.origin).searchParams.get('limit')) || 4;
    const items = [
      ...complementaryList.querySelectorAll(':scope > .resource-list__item'),
      ...relatedList.querySelectorAll(':scope > .resource-list__item'),
    ];
    const productPaths = new Set();
    const prioritizedItems = [];

    for (const item of items) {
      const productLink = item.querySelector('a.product-card__link[href]');
      const productPath = productLink ? new URL(productLink.href, window.location.origin).pathname : '';

      if (!productPath || productPaths.has(productPath)) continue;

      productPaths.add(productPath);
      prioritizedItems.push(item.cloneNode(true));

      if (prioritizedItems.length >= maxProducts) break;
    }

    prioritizedItems.forEach((item, index) => this.#namespaceItemIds(item, index));
    relatedList.replaceChildren(...prioritizedItems);
    relatedList.dataset.hasRecommendations = prioritizedItems.length ? 'true' : 'false';

    return relatedRecommendations;
  }

  /**
   * Keeps IDs and their local references unique after combining two rendered sections.
   * @param {Element} item
   * @param {number} index
   */
  #namespaceItemIds(item, index) {
    const idMap = new Map();

    for (const element of item.querySelectorAll('[id]')) {
      const oldId = element.id;
      const newId = `${oldId}-priority-${index + 1}`;
      idMap.set(oldId, newId);
      element.id = newId;
    }

    const tokenAttributes = ['aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns'];
    const exactAttributes = ['for', 'form', 'list', 'popovertarget'];

    for (const element of item.querySelectorAll('*')) {
      for (const attribute of tokenAttributes) {
        if (!element.hasAttribute(attribute)) continue;
        const value = element.getAttribute(attribute) || '';
        element.setAttribute(
          attribute,
          value
            .split(/\s+/)
            .map((token) => idMap.get(token) || token)
            .join(' ')
        );
      }

      for (const attribute of exactAttributes) {
        const value = element.getAttribute(attribute);
        if (value && idMap.has(value)) element.setAttribute(attribute, idMap.get(value));
      }

      const href = element.getAttribute('href');
      if (href?.startsWith('#') && idMap.has(href.slice(1))) {
        element.setAttribute('href', `#${idMap.get(href.slice(1))}`);
      }
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {(string | undefined)[]} intents
   * @returns {Promise<{ success: true, data: string[] } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intents) {
    const urls = intents.map(
      (intent) => `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`
    );
    this.#activeFetch?.abort();
    const activeFetch = new AbortController();
    this.#activeFetch = activeFetch;

    try {
      const responses = await Promise.all(
        urls.map(async (url) => {
          if (this.#cachedRecommendations[url]) {
            return { success: true, data: this.#cachedRecommendations[url] };
          }

          const response = await fetch(url, { signal: activeFetch.signal });
          if (!response.ok) return { success: false, status: response.status };

          const data = await response.text();
          this.#cachedRecommendations[url] = data;
          return { success: true, data };
        })
      );
      const failedResponse = responses.find((response) => !response.success);

      if (failedResponse && !failedResponse.success) {
        return { success: false, status: failedResponse.status };
      }

      return { success: true, data: responses.map((response) => response.data) };
    } finally {
      if (this.#activeFetch === activeFetch) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
